import {
  useState,
  useCallback,
  useRef,
  useEffect,
  createContext,
  useContext,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance, { setupAxiosInterceptors } from "app/services/api";
import { setAuthUser, clearAuthUser } from "app/redux/action/user";
import { message } from "antd";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [isLoading, setIsLoading] = useState(true);
  const accessTokenRef = useRef(null);

  const getAccessToken = useCallback(() => accessTokenRef.current, []);

  const setAccessToken = useCallback((token) => {
    accessTokenRef.current = token;
  }, []);

  const logoutActionRef = useRef(null);

  const clearSession = useCallback(() => {
    accessTokenRef.current = null;
    delete axiosInstance.defaults.headers.common["Authorization"];
    dispatch(clearAuthUser());
  }, [dispatch]);

  const forceLogoutAction = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const logoutAction = useCallback(async () => {
    const userId = logoutActionRef.current?.userId;
    const token = accessTokenRef.current;

    // Lấy endpoint của Web Push subscription hiện tại trước khi xóa session
    let pushEndpoint = null;
    try {
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration && registration.pushManager) {
          const subscription = await registration.pushManager.getSubscription();
          pushEndpoint = subscription?.endpoint || null;
        }
      }
    } catch (error) {
      message.error(
        error.message || "Lấy endpoint của Web Push subscription thất bại!",
      );
    }

    clearSession();
    if (!userId) return;

    try {
      await axiosInstance.put(
        `/users/auth/sign-out/${userId}`,
        pushEndpoint ? { endpoint: pushEndpoint } : {},
        {
          _skipAuthRefresh: true,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        },
      );
    } catch (error) {
      message.error(error.response?.data?.message || "Đăng xuất thất bại!");
    }
  }, [clearSession]);

  logoutActionRef.current = {
    fn: logoutAction,
    userId: user?.id,
  };

  useEffect(() => {
    setupAxiosInterceptors({
      getAccessToken,
      setAccessToken,
      onLogout: () => logoutActionRef.current?.fn(),
      onAuthExpired: forceLogoutAction,
    });

    const initSession = async () => {
      try {
        const response = await axiosInstance.post("users/auth/refresh-token");
        const { accessToken, user: userData } = response;
        accessTokenRef.current = accessToken;
        axiosInstance._markSessionActive?.();
        dispatch(setAuthUser(userData));
      } catch {
        dispatch(clearAuthUser());
      } finally {
        setIsLoading(false);
      }
    };

    initSession();
  }, [dispatch, forceLogoutAction, getAccessToken, setAccessToken]);

  const signinAction = useCallback(
    async (phone, password, customDeviceId, customDeviceInfo) => {
      // Automatically detect device ID and info if not provided
      let deviceId = customDeviceId || null;
      let deviceInfo = customDeviceInfo || null;

      if (typeof window !== "undefined") {
        if (!deviceId) {
          try {
            deviceId = window.localStorage.getItem("deviceId");
            if (!deviceId) {
              deviceId =
                "web_" +
                Math.random().toString(36).substring(2, 15) +
                "_" +
                Date.now();
              window.localStorage.setItem("deviceId", deviceId);
            }
          } catch (e) {
            console.warn("localStorage is not available for deviceId: ", e);
          }
        }

        if (!deviceInfo) {
          deviceInfo = {
            userAgent: window.navigator.userAgent,
            platform: window.navigator.platform,
            language: window.navigator.language,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };
        }
      }

      const response = await axiosInstance.post("users/auth/sign-in", {
        phone,
        password,
        deviceId,
        deviceInfo,
      });
      const { accessToken, user: userData } = response;
      accessTokenRef.current = accessToken;
      axiosInstance._markSessionActive?.();
      dispatch(setAuthUser(userData));

      return userData;
    },
    [dispatch],
  );

  const refreshSession = useCallback(async () => {
    try {
      const response = await axiosInstance.post("users/auth/refresh-token");
      const { accessToken, user: userData } = response;
      accessTokenRef.current = accessToken;
      axiosInstance._markSessionActive?.();
      dispatch(setAuthUser(userData));
    } catch {
      // Optional: ignore error, interceptor handles logout if failed
    }
  }, [dispatch]);

  const value = {
    user,
    isLoading,
    signinAction,
    logoutAction,
    getAccessToken,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
